/* eslint-disable react/jsx-props-no-spreading */
import { usePrevious } from '@dnd-kit/utilities';
import { faDiagramPredecessor, faTableList } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { inMarketTheme } from '@inmarket/inmarkit';
import { Button, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { filterColumnsByColumnIdsAndSectionType, getLocationsColumns } from '../../constants/reportTemplateColumnList';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { ColumnsSchema, SectionSchema, SectionType, Sheet } from '../../validationSchemas/ReportTemplateSchema';
import Checkbox from '../forms/Checkbox';
import RadioGroup from '../forms/RadioGroup';
import TextField from '../forms/TextField';
import ColumnList from './ColumnList';
import ColumnSelector from './ColumnSelector';
import { useReportingHubContext } from './useReportingHubContext';

interface SectionFormProps {
  isEdit: boolean;
  sheet: Sheet;
  onChange: Function;
}

function SectionForm({ sheet, isEdit, onChange }: SectionFormProps) {
  const setValueOptions = {
    shouldDirty: true,
    shouldValidate: true,
  };
  const { activeSection, setActiveSection } = useReportingHubContext();
  const [disabledColumnIds, setDisabledColumnIds] = useState([]);

  const form = useForm({
    resolver: zodResolver(SectionSchema),
    defaultValues: activeSection,
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid },
    setError,
    watch,
    setValue,
    clearErrors,
    getValues,
  } = form;

  const submitHandler = (section) => {
    if (
      sheet.reportSections
        ?.filter((sectionItem) => sectionItem.sectionName !== activeSection.sectionName)
        .map((sectionItem) => sectionItem.sectionName.toLowerCase())
        .includes(section.sectionName.toLowerCase())
    ) {
      setError('sectionName', { type: 'custom', message: 'Please make sure the section name is unique' });
      return;
    }

    // Perform a check to see if the columns are valid based on the section type.
    const columnObjectList = filterColumnsByColumnIdsAndSectionType(section.columns, section.sectionType);
    const columns = columnObjectList.map((column) => column.id);
    try {
      ColumnsSchema.parse(columns);
    } catch (e) {
      const message = JSON.parse(e.message);
      setError('columns', { type: 'custom', message: message[0].message });
      setTimeout(() => clearErrors('columns'), 1000);
      return;
    }

    // Remove all empty column labels.
    const { columnLabels } = section;
    if (columnLabels) {
      Object.keys(columnLabels).forEach((columnLabel) => {
        if (!columnLabels[columnLabel]) {
          delete columnLabels[columnLabel];
        }
      });
    }

    const newSection = {
      ...section,
      columnLabels: isEmpty(columnLabels) ? null : columnLabels,
      columns,
    };

    onChange(newSection);
  };

  const cancel = () => {
    setActiveSection(null);
  };

  useConfirmNavChange(() => isDirty);

  const sectionTypeOptions = Object.values(SectionType).map((item) => ({
    value: item,
    label: item,
  }));

  const [sectionType, columns] = watch(['sectionType', 'columns']);

  const validateColumns = (columnList, columnId, isAdd: boolean) => {
    if (columnId) {
      if (!isAdd) {
        // Removed the renamed label for the column if it exists.
        const columnLabels = getValues('columnLabels');
        if (columnLabels && columnLabels[columnId]) {
          delete columnLabels[columnId];
          setValue('columnLabels', columnLabels, setValueOptions);
        }

        // Also remove it from sortBy and sortAscending if they exist.
        const sortBy = getValues('sortBy') || [];
        const sortAscending = getValues('sortAscending') || [];
        const indexOfColumnId = sortBy.indexOf(columnId);
        if (indexOfColumnId >= 0) {
          sortBy.splice(indexOfColumnId, 1);
          sortAscending.splice(indexOfColumnId, 1);
          setValue('sortBy', sortBy, setValueOptions);
          setValue('sortAscending', sortAscending, setValueOptions);
        }
      }
    }

    const locationColumnIds = getLocationsColumns().map((column) => column['Column ID']);
    // Disable the location columns if there is a location added already.
    if (locationColumnIds.includes(columnId) && columnList.find((id) => locationColumnIds.includes(id))) {
      setDisabledColumnIds(locationColumnIds);
    } else {
      setDisabledColumnIds([]);
    }
  };

  const onColumnChange = (columnList, columnId = null) => {
    const isAdd = columnList.includes(columnId);

    // Special case where we must add the state if city is selected.
    if (columnId === 'city_name' && isAdd) {
      columnList.push('state_name');
    }
    setValue('columns', columnList, setValueOptions);
    validateColumns(columnList, columnId, isAdd);
  };

  if (!isEmpty(form.formState?.errors)) {
    // eslint-disable-next-line no-console
    console.log(form.formState?.errors);
  }

  const previousSectionType = usePrevious(sectionType);

  // Clear the sorting if user changes section type.
  useEffect(() => {
    if (sectionType !== previousSectionType && previousSectionType !== undefined) {
      setValue('sortBy', [], {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('sortAscending', [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [sectionType, previousSectionType, setValue]);

  const style1 = {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '48px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  };

  const style2 = {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.17px',
  };

  return (
    <FormProvider {...form}>
      <Stack justifyContent="space-between" height="100%">
        <Stack spacing={2} sx={{ overflowY: 'scroll' }} pb={10}>
          <Stack px={2} direction="row" justifyContent="space-between">
            <Typography sx={style1}>Section Settings</Typography>
            <Stack direction="row" spacing={-1}>
              <Tooltip title="Partition By IO">
                <Checkbox
                  label=""
                  name="partitionByIo"
                  icon={<FontAwesomeIcon icon={faTableList} />}
                  checkedIcon={<FontAwesomeIcon icon={faTableList} color={inMarketTheme.palette.primary.main} />}
                />
              </Tooltip>
              <Tooltip title="Include Subtitles">
                <Checkbox
                  label=""
                  name="includeSubtotals"
                  icon={<FontAwesomeIcon icon={faDiagramPredecessor} />}
                  checkedIcon={
                    <FontAwesomeIcon icon={faDiagramPredecessor} color={inMarketTheme.palette.primary.main} />
                  }
                />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack px={2}>
            <TextField label="Section Name" name="sectionName" size="small" />
          </Stack>
          <Divider />
          <Stack px={2}>
            <Typography sx={style2}>Section Type</Typography>
            <RadioGroup options={sectionTypeOptions} name="sectionType" />
          </Stack>
          <Divider />
          <Stack>
            <ColumnList sectionType={sectionType} name="columns" onChange={onColumnChange} />
          </Stack>
          <Divider />
          <Stack>
            <ColumnSelector
              sectionType={sectionType}
              options={columns}
              onChange={onColumnChange}
              open={!isEdit}
              disabledColumnIds={disabledColumnIds}
            />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          p={2}
          spacing={1}
          justifyContent="center"
          className="reporting-hub-sticky-CTA"
        >
          <Button variant="outlined" onClick={cancel} fullWidth>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(submitHandler)}
            disabled={!isDirty || !isValid}
            aria-label={`${isValid ? 'Enabled' : 'Disabled'} Done`}
            fullWidth
          >
            Done
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
}

export default SectionForm;
